import React from "react";

type ButtonVariant = "filled" | "outline" | "text" | "success" | "danger";
type ButtonSize = "small" | "medium" | "large" | "none";
type ButtonType = "submit" | "reset" | "button";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children?: React.ReactNode;
  buttonType?: ButtonType;
}

const Button: React.FC<ButtonProps> = ({
  label,
  className = "",
  onClick,
  variant = "filled",
  size = "large",
  disabled = false,
  startIcon,
  endIcon,
  children,
  buttonType = "button",
  ...buttonProps
}) => {
  const baseStyle =
    "rounded-lg font-semibold transition duration-300 flex items-center justify-center gap-2";

  const variantStyles: Record<ButtonVariant, string> = {
    filled: "bg-primary text-pd-cream hover:bg-hover-green hover:text-pd-ink",
    outline: "border border-primary text-gray-700 hover:bg-hover-green",
    text: "bg-none text-primary hover:underline",
    success: "bg-[#155724] text-white hover:bg-[#0f3d18]",
    danger: "bg-[#DC3545] text-white hover:bg-[#B02A37]",
  };

  const disabledStyles: Record<ButtonVariant, string> = {
    filled:
      "opacity-50 !bg-pd-rule border-none hover:bg-pd-rule cursor-not-allowed !text-pd-mist",
    outline: "opacity-50 cursor-not-allowed text-pd-mist",
    text: "opacity-50 cursor-not-allowed !bg-white", // No background
    success: "opacity-50 !bg-pd-rule cursor-not-allowed text-pd-mist",
    danger: "opacity-50 !bg-pd-rule cursor-not-allowed text-pd-mist",
  };

  const sizeStyles: Record<ButtonSize, string> = {
    small: "px-3 py-2 text-p",
    medium: "px-4 py-3 py-2 text-h6",
    large: "px-6 py-3.5 text-h6",
    none: "",
  };

  return (
    <button
      {...buttonProps}
      className={` flex items-center
    ${baseStyle}
    ${variantStyles[variant]}
    ${sizeStyles[size]}
    ${disabled ? disabledStyles[variant] : ""}
    ${className}
  `}
      onClick={onClick}
      disabled={disabled}
      type={buttonType}
    >
      {startIcon && <span>{startIcon}</span>}
      {children
        ? children
        : label && (
          <span className="flex text-p leading-4 text-inherit">
            {label}
          </span>
        )}
      {endIcon && <span>{endIcon}</span>}
    </button>
  );
};

export default Button;
